import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface User {
  id: number;
  username: string;
  email: string;
  emailVerified?: boolean;
  emailVerifiedAt?: string;
  profilePhoto: string | null;
  birthdate?: string;
  gender?: string;
  socialPreference?: string;
  age?: number;
  drugUsePreference?: string;
  bio?: string;
  createdAt?: string;
  isAdmin?: boolean;
  chatEnhancements?: boolean;
  profileCompletion?: {
    percentage: number;
    missingFields: string[];
  };
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthGetters {
  token: string | null;
}

interface AuthActions {
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  updateUser: (updates: Partial<User>) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  clearError: () => void;
}

type AuthStore = AuthState & AuthGetters & AuthActions;

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Getters
      get token() {
        const state = get();
        return state?.accessToken || null;
      },

      setAuth: (user, accessToken, refreshToken) => {
        
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          error: null,
        });
        
        // Check if data was actually saved to localStorage
        setTimeout(() => {
          const saved = localStorage.getItem('solofix-auth');
          if (saved) {
            try {
              JSON.parse(saved);
              // Data successfully parsed from localStorage
            } catch (e) {
              console.error("🔒 AUTH: Failed to parse localStorage data:", e);
            }
          }
        }, 100);
      },

      setUser: (user) => {
        set({ user });
      },

      updateUser: (updates: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      },

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setError: (error) => {
        set({ error });
      },

      logout: () => {
        set({
          ...initialState,
        });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: "solofix-auth",
      // Tokens are intentionally NOT persisted: they live in httpOnly cookies
      // (sent via withCredentials) and, for the access token, in memory only.
      // Only non-secret UI state is persisted for an optimistic logged-in view;
      // the cookie is what actually authenticates each request.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        return (_state, error) => {
          if (error) {
            console.error("🚨 Zustand: Rehydration failed:", error);
          } else {
            // Rehydration completed successfully
          }
        };
      },
    },
  ),
);
